import { COVERAGE_TARGETS, TOPIC_CORE, TIER_MIX, realmTarget, tierOfTopic, type CoverageTarget } from '../../../../shared/src/taxonomyV2';
import type { RealmStats } from './AdminContext';

/**
 * Everything the factory screen needs to answer "what should I generate next",
 * computed in one place so the header, the realm grid and the topic table can
 * never disagree about the gap.
 *
 * The unit is the topic. A realm total hides the only thing that matters: a
 * realm can hold 1,900 questions and still have topics sitting at zero, and the
 * player who picks that topic sees the same handful every match.
 */

export interface TopicGap {
  topic: string;
  tier: ReturnType<typeof tierOfTopic>;
  have: number;
  target: number;
  gap: number;
  /** Per-difficulty shortfall against the tier budget, easy/medium/hard. */
  byDiff: [number, number, number];
}

export interface RealmGap {
  realm: string;
  have: number;
  target: number;
  gap: number;
  /** Topics still under target. */
  thin: number;
  /** Topics with nothing at all. The worst case for a player. */
  empty: number;
  topics: TopicGap[];
}

export function realmGap(realm: string, stats: RealmStats | undefined, target: CoverageTarget): RealmGap {
  const topics = (TOPIC_CORE[realm] ?? []).map<TopicGap>(topic => {
    const have = stats?.subTopics?.[topic] ?? 0;
    const tier = tierOfTopic(topic);
    const mix = TIER_MIX[tier];
    const d = stats?.subTopicDiffs?.[topic];
    const haveByDiff: [number, number, number] = [d?.easy ?? 0, d?.medium ?? 0, d?.hard ?? 0];
    // Shortfall is measured per difficulty band, because a topic sitting at
    // target with everything in "easy" still has nothing for a strong player.
    const byDiff = ([0, 1, 2] as const).map(i =>
      Math.max(0, Math.round(target.perTopic * mix[i]) - haveByDiff[i])
    ) as [number, number, number];
    return {
      topic, tier, have,
      target: target.perTopic,
      gap: byDiff[0] + byDiff[1] + byDiff[2],
      byDiff,
    };
  });
  return {
    realm,
    have: stats?.total ?? 0,
    target: realmTarget(target, realm),
    gap: topics.reduce((s, t) => s + t.gap, 0),
    thin: topics.filter(t => t.gap > 0).length,
    empty: topics.filter(t => t.have === 0).length,
    topics,
  };
}

export function poolGaps(stats: Record<string, RealmStats>, target: CoverageTarget): RealmGap[] {
  return Object.keys(TOPIC_CORE).map(r => realmGap(r, stats[r], target));
}

/** The first target this realm has not reached yet, or null when all are met. */
export function nextTargetFor(have: number, realm: string): CoverageTarget | null {
  return COVERAGE_TARGETS.find(t => have < realmTarget(t, realm)) ?? null;
}
